import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, Button, Accordion, Form, Collapse } from 'react-bootstrap';
import { showConfirm } from 'react-confirm-prompt';
import sectionApi from '../../services/sectionapi';
import designThemeApi from '../../services/designThemeApi';
import SectionForm from './SectionForm';
import './SectionList.css';
import EditItemModal from './EditItemModal';
import Loader from '../loader/loader';
import BannerEditModal from './BannerEditModal';

const sectionTypeMap = {
  1: 'Main Banner',
  2: 'Products Card',
  3: 'Banner',
  4: 'Brands',
  5: 'Banner with Two Row Products'
};

const sectionForMap = {
  1: '🏠 Home Page',
  2: '🔥 Offer Page'
};

function SectionList() {
  const [themes, setThemes] = useState([]);
  const [themeId, setThemeId] = useState(() => { const id = Number(new URLSearchParams(window.location.search).get('theme_id')); return Number.isSafeInteger(id) && id > 0 ? id : 1; });
  const [sectionFor, setSectionFor] = useState(() => new URLSearchParams(window.location.search).get('section_for') === '2' ? 2 : 1);

  const [sections, setSections] = useState([]);
  const [products, setProducts] = useState([]);

  const [editingSection, setEditingSection] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [selectedItem, setSelectedItem] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaderVisible, setLoaderVisible] = useState(false);

  const [showBannerEditModal, setShowBannerEditModal] = useState(false);
  const [openSections, setOpenSections] = useState({});

  const toggleSectionOpen = (id) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // --- ordering helpers ---
  const getNum = (v, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const sortByOrder = (arr = [], key = 'order_by') => {
    const withIndex = (arr || []).map((x, idx) => ({ ...x, __idx: idx }));
    return withIndex
      .sort((a, b) => {
        const ao = getNum(a[key], a.__idx + 1);
        const bo = getNum(b[key], b.__idx + 1);
        return ao - bo;
      })
      .map(({ __idx, ...rest }) => rest);
  };

  const activeThemes = useMemo(() => (themes || []).filter((t) => t.status !== 9), [themes]);
  const [creatingRowForSectionId, setCreatingRowForSectionId] = useState(null);
  const [creatingRow, setCreatingRow] = useState(false);


  const buildTempRow = (secId, existingRowsCount) => ({
    id: `tmp_${Date.now()}`,
    homesecID: secId,
    title: `Row ${existingRowsCount + 1}`,
    noofcols: 1,
    status: 1,
    homedata: []
  });
  const handleAddRow = async (sec) => {
    const confirmed = await showConfirm('Add a new row?', {
      description: 'A new row will be created under this section.',
      type: 'info'
    });
    if (!confirmed) return;

    setCreatingRowForSectionId(sec.id);
    setCreatingRow(true);

    // ✅ optimistic row (show instantly)
    const tempRow = buildTempRow(sec.id, (sec.homerow || []).length);

    setSections((prev) =>
      prev.map((s) =>
        s.id === sec.id
          ? {
            ...s,
            homerow: [...(s.homerow || []), tempRow],
            noofrows: (parseInt(s.noofrows || 0) || 0) + 1
          }
          : s
      )
    );

    try {
      const res = await sectionApi.createRow({
        homesecID: sec.id,
        title: tempRow.title,
        noofcols: 1,
        status: 1
      });

      if (!res?.success) {
        // ❌ revert optimistic UI if failed
        setSections((prev) =>
          prev.map((s) =>
            s.id === sec.id
              ? {
                ...s,
                homerow: (s.homerow || []).filter((r) => String(r.id) !== String(tempRow.id)),
                noofrows: Math.max((parseInt(s.noofrows || 1) || 1) - 1, 0)
              }
              : s
          )
        );
        console.error('Create row failed:', res?.message);
        return;
      }

      // ✅ if backend returns row, replace temp row (optional)
      if (res?.data?.id) {
        const createdRow = res.data;
        setSections((prev) =>
          prev.map((s) =>
            s.id === sec.id
              ? {
                ...s,
                homerow: (s.homerow || []).map((r) =>
                  String(r.id) === String(tempRow.id) ? createdRow : r
                )
              }
              : s
          )
        );
      }

      // ✅ final refresh from server
      await fetchSections();
    } catch (e) {
      // revert on exception
      setSections((prev) =>
        prev.map((s) =>
          s.id === sec.id
            ? {
              ...s,
              homerow: (s.homerow || []).filter((r) => String(r.id) !== String(tempRow.id)),
              noofrows: Math.max((parseInt(s.noofrows || 1) || 1) - 1, 0)
            }
            : s
        )
      );
      console.error('Add row error:', e);
    } finally {
      setCreatingRow(false);
      setCreatingRowForSectionId(null);
    }
  };

  const fetchThemes = async () => {
    try {
      const res = await designThemeApi.listThemes();
      if (res?.success) setThemes(res.data || []);
    } catch (e) {
      console.error('Theme fetch error', e);
    }
  };

  const fetchSections = async () => {
    try {
      const res = await sectionApi.getAllSections({ theme_id: themeId, section_for: sectionFor });
      if (res?.success && Array.isArray(res.data)) {
        setSections(res.data);
        setProducts(res.products || []);
      } else {
        setSections([]);
      }
    } catch (err) {
      console.error('Error fetching sections:', err);
      setSections([]);
    }
  };

  useEffect(() => {
    fetchThemes();
  }, []);

  useEffect(() => {
    fetchSections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeId, sectionFor]);

  useEffect(() => {
    setOpenSections((prev) => {
      const next = { ...prev };
      (sections || []).forEach((s) => {
        if (next[s.id] === undefined) next[s.id] = true; // default open
      });
      return next;
    });
  }, [sections]);

  const handleAdd = () => {
    setEditingSection(null);
    setShowForm(true);
  };

  const handleEdit = (section) => {
    setEditingSection(section);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm('Delete this section?', {
      description: 'This will remove the section.',
      type: 'info'
    });
    if (!confirmed) return;

    setLoaderVisible(true);
    try {
      await sectionApi.deleteSection(id);
      await fetchSections();
    } finally {
      setLoaderVisible(false);
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    fetchSections();
  };

  const handleItemEdit = (item) => {
    setSelectedItem(item);
    setShowEditModal(true);
  };

  const handleAddItem = (rowId) => {
    setSelectedItem({
      id: null,
      title: '',
      slug: '',
      url: '',
      actionIdType: 1,
      actionID: '',
      status: 1,
      homesecListID: rowId
    });
    setShowEditModal(true);
  };

  const handleModalClose = () => {
    setShowEditModal(false);
    setSelectedItem(null);
    fetchSections();
  };

  const handleItemSave = async (updatedData) => {
    setSaving(true);
    try {
      const fd = new FormData();

      // common fields
      fd.append('title', updatedData.title || '');
      fd.append('slug', updatedData.slug || '');
      fd.append('url', updatedData.url || '');
      fd.append('actionIdType', updatedData.actionIdType);
      fd.append('actionID', updatedData.actionID || '');
      fd.append('status', updatedData.status);
      if (updatedData.files) fd.append('files', updatedData.files);

      let response = null;

      if (updatedData.id) {
        fd.append('id', updatedData.id);
        response = await sectionApi.updateItem(fd);
      } else {
        // create new item in a row
        fd.append('homesecListID', updatedData.homesecListID);
        response = await sectionApi.createItem(fd);
      }

      if (response?.success) {
        setShowEditModal(false);
        setSelectedItem(null);
        fetchSections();
      }
    } catch (err) {
      console.error('Error saving item:', err);
    } finally {
      setSaving(false);
    }
  };

  const renderStatusBadge = (status) =>
    status === 2 ? <span className="badge bg-danger ms-2">Inactive</span> : <span className="badge bg-success ms-2">Active</span>;

  const handleStatusToggle = async (targetType, id, currentStatus) => {
    const newStatus = currentStatus === 1 ? 2 : 1;

    const confirmed = await showConfirm(
      currentStatus === 1 ? 'Deactivate?' : 'Activate?',
      {
        description: currentStatus === 1 ? 'This will hide it from users.' : 'This will make it visible to users.',
        type: 'info'
      }
    );
    if (!confirmed) return;

    setLoaderVisible(true);
    try {
      if (targetType === 'homedata') {
        const fd = new FormData();
        fd.append('id', id);
        fd.append('status', newStatus);
        const resp = await sectionApi.updateItem(fd);
        if (resp?.success) fetchSections();
      } else if (targetType === 'row') {
        const resp = await sectionApi.updateListStatus({ id, status: newStatus });
        if (resp?.success) fetchSections();
      } else if (targetType === 'section') {
        const fd = new FormData();
        fd.append('id', id);
        fd.append('status', newStatus);
        const resp = await sectionApi.updateSectionData(fd);
        if (resp?.success) fetchSections();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoaderVisible(false);
    }
  };

  const getProductDetails = (item) => {
    if (item.actionIdType === 1) {
      const flatProducts = (products || []).flat();
      const product = flatProducts.find((p) => String(p.action_id) === String(item.actionID));
      const itemImage = item.valueData ?? '';
      return product ? (
        <div className="d-flex align-items-center">
          <img
            src={itemImage ? process.env.REACT_APP_IMAGE_URL + itemImage : `${process.env.REACT_APP_IMAGE_URL}/uploads/${product.image?.[1]}`}
            alt={product.productName}
            style={{ width: 60, height: 60, objectFit: 'cover', marginRight: 10, borderRadius: 4 }}
          />
          <div>
            <strong>{product.productName || 'Unnamed Product'}</strong>
            <br />
            <small className="text-muted">
              {product.unit} {product.unitName}
            </small>
            <br />
            <small className="text-primary">{product.brandName}</small>
          </div>
        </div>
      ) : (
        <span className="text-danger">⚠️ Product not found for actionID: {item.actionID}</span>
      );
    }

    return (
      <>
        <strong>{item.slug || 'Unnamed'}</strong>
        <span className="text-muted ms-2">({item.url})</span>
      </>
    );
  };

  const handleEditBanner = (section) => {
    setEditingSection(section);
    setShowBannerEditModal(true);
  };

  // --- ORDERING ACTIONS (Section / Row / Item) ---
  const moveSection = async (sectionId, direction) => {
    const ordered = sortByOrder(sections, 'order_by');
    const idx = ordered.findIndex((s) => String(s.id) === String(sectionId));
    const swapWith = direction === 'up' ? idx - 1 : idx + 1;
    if (idx < 0 || swapWith < 0 || swapWith >= ordered.length) return;

    const a = ordered[idx];
    const b = ordered[swapWith];

    const aOrder = getNum(a.order_by, idx + 1);
    const bOrder = getNum(b.order_by, swapWith + 1);

    setLoaderVisible(true);
    try {
      await Promise.all([
        sectionApi.updateSection({ id: a.id, order_by: bOrder }),
        sectionApi.updateSection({ id: b.id, order_by: aOrder }),
      ]);
      await fetchSections();
    } catch (e) {
      console.error('Section reorder error:', e);
    } finally {
      setLoaderVisible(false);
    }
  };

  const moveRow = async (sec, rowId, direction) => {
    const rows = sec?.homerow || [];
    const orderedRows = sortByOrder(rows, 'orderList');
    const idx = orderedRows.findIndex((r) => String(r.id) === String(rowId));
    const swapWith = direction === 'up' ? idx - 1 : idx + 1;
    if (idx < 0 || swapWith < 0 || swapWith >= orderedRows.length) return;

    const a = orderedRows[idx];
    const b = orderedRows[swapWith];

    const aOrder = getNum(a.orderList, idx + 1);
    const bOrder = getNum(b.orderList, swapWith + 1);

    setLoaderVisible(true);
    try {
      await Promise.all([
        sectionApi.updateListStatus({ id: a.id, orderList: bOrder }),
        sectionApi.updateListStatus({ id: b.id, orderList: aOrder }),
      ]);
      await fetchSections();
    } catch (e) {
      console.error('Row reorder error:', e);
    } finally {
      setLoaderVisible(false);
    }
  };

  const moveItem = async (row, itemId, direction) => {
    const items = row?.homedata || [];
    const orderedItems = sortByOrder(items, 'orderList');
    const idx = orderedItems.findIndex((it) => String(it.id) === String(itemId));
    const swapWith = direction === 'up' ? idx - 1 : idx + 1;
    if (idx < 0 || swapWith < 0 || swapWith >= orderedItems.length) return;

    const a = orderedItems[idx];
    const b = orderedItems[swapWith];

    const aOrder = getNum(a.orderList, idx + 1);
    const bOrder = getNum(b.orderList, swapWith + 1);

    setLoaderVisible(true);
    try {
      const fdA = new FormData();
      fdA.append('id', a.id);
      fdA.append('orderList', bOrder);

      const fdB = new FormData();
      fdB.append('id', b.id);
      fdB.append('orderList', aOrder);

      await Promise.all([sectionApi.updateItem(fdA), sectionApi.updateItem(fdB)]);
      await fetchSections();
    } catch (e) {
      console.error('Item reorder error:', e);
    } finally {
      setLoaderVisible(false);
    }
  };

  return (
    <Container className="my-4">
      <Row className="mb-3">
        <Col>
          <div className="section-page-header">
            <div>
              <h2 className="section-page-title">Web Page Sections</h2>
              <div className="section-page-subtitle">Theme-based builder</div>
            </div>

            <div className="section-header-actions">
              <Form.Select
                size="sm"
                value={themeId}
                onChange={(e) => setThemeId(parseInt(e.target.value))}
                style={{ width: 220 }}
              >
                {activeThemes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.code})
                  </option>
                ))}
              </Form.Select>

              <Form.Select
                size="sm"
                value={sectionFor}
                onChange={(e) => setSectionFor(parseInt(e.target.value))}
                style={{ width: 160 }}
              >
                <option value={1}>Home Page</option>
                <option value={2}>Offer Page</option>
              </Form.Select>

              <Button size="sm" className="btn-action" onClick={handleAdd}>
                + Create Section
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {loaderVisible && <Loader />}

      {sortByOrder(sections, 'order_by').map((sec) => (
        <Card key={sec.id} className="mb-4 shadow-sm">
          <div className="p-2 bg-light border-bottom text-muted small">
            {sectionForMap[sec.sectionFor] || '📄 Unknown Page'} | Theme ID: {sec.themeId || themeId}
          </div>

          <Card.Header
            className="align-items-center d-flex"
            style={{ cursor: 'pointer' }}
            onClick={() => toggleSectionOpen(sec.id)}
          >
            <Col md={7}>
              <h5 className="mb-0">
                {sectionTypeMap[sec.secType] || 'Unknown'}
                {sec.title ? ` | Title: ${sec.title}` : ''}
                {renderStatusBadge(sec.status)}
                <span className="badge bg-secondary ms-2">Order: {getNum(sec.order_by, '-') || '-'}</span>
                <span className="ms-2 text-muted">{openSections[sec.id] ? '▾' : '▸'}</span>
                {parseInt(sec.seeAll) === 1 ? <span className="badge bg-info ms-2">See All</span> : null}

              </h5>
              <small>No. of Rows: {sec.noofrows}</small>
            </Col>

            <Col md={5} className="section-header-actions">
              <Button
                variant="outline-secondary"
                size="sm"
                className="btn-action"
                title="Move up"
                onClick={(e) => { e.stopPropagation(); moveSection(sec.id, 'up'); }}
              >
                ↑
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                className="btn-action"
                title="Move down"
                onClick={(e) => { e.stopPropagation(); moveSection(sec.id, 'down'); }}
              >
                ↓
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                className="btn-action"
                disabled={creatingRow && creatingRowForSectionId === sec.id}
                onClick={(e) => { e.stopPropagation(); handleAddRow(sec); }}
              >
                {creatingRow && creatingRowForSectionId === sec.id ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    Adding...
                  </>
                ) : (
                  '+ Add Row'
                )}
              </Button>

              <Button variant="outline-primary" size="sm" className="btn-action" onClick={(e) => { e.stopPropagation(); handleEdit(sec); }}>
                Edit Section
              </Button>
              <Button
                variant={sec.status === 1 ? 'outline-danger' : 'outline-success'}
                size="sm"
                className="btn-action"
                onClick={(e) => { e.stopPropagation(); handleStatusToggle('section', sec.id, sec.status); }}

              >
                {sec.status === 1 ? 'Deactivate' : 'Activate'}
              </Button>
              <Button variant="outline-secondary" size="sm" className="btn-action" onClick={(e) => { e.stopPropagation(); handleDelete(sec.id); }}>
                Delete
              </Button>
            </Col>
          </Card.Header>
          <Collapse in={!!openSections[sec.id]}>
            <div>
              <Card.Body>
                <Row className="mb-3 align-items-center">
                  <Col md={8}>
                    <div className="d-flex align-items-center">
                      <img
                        src={`${process.env.REACT_APP_IMAGE_URL}${sec.bannerImg}`}
                        alt="Section Banner"
                        className="img-fluid rounded"
                        style={{ maxHeight: '150px', marginRight: 10 }}
                      />
                      <div>
                        <div className="mb-1">
                          <strong>See All:</strong>{' '}
                          {parseInt(sec.seeAll) === 1 && sec.url ? (
                            <a
                              href={`${process.env.REACT_APP_WEBSITE_URL}${sec.url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {sec.url}
                            </a>
                          ) : (
                            <span className="text-muted">Disabled</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col md={4} className="text-end">
                    <Button
                      variant="info"
                      size="sm"
                      className="btn-action"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditBanner(sec);
                      }}
                    >
                      ✏️ Edit Banner
                    </Button>
                  </Col>
                </Row>

                <Accordion>
                  {sortByOrder(sec.homerow || [], 'orderList').map((row, idx) => (
                    <Accordion.Item eventKey={idx.toString()} key={row.id}>
                      <Accordion.Header>
                        <div className="d-flex w-100 align-items-center justify-content-between">
                          <div className='d-flex align-items-center gap-2'>
                            🧩 Row {idx + 1} | Columns: {row.noofcols}
                            <span className="badge bg-secondary">Order: {getNum(row.orderList, idx + 1)}</span>
                            {row.title ? ` | Title: ${row.title}` : ''}
                            {row.status === 2 && <span className="text-danger fw-semibold ms-3">🔴 Inactive</span>}
                            {row.status === 1 && <span className="text-success fw-semibold ms-3">🟢 Active</span>}
                          </div>
                          <div className='d-flex align-items-center gap-2 mx-3'>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="btn-action"
                              title="Move row up"
                              onClick={(e) => {
                                e.stopPropagation();
                                moveRow(sec, row.id, 'up');
                              }}
                            >
                              ↑
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="btn-action"
                              title="Move row down"
                              onClick={(e) => {
                                e.stopPropagation();
                                moveRow(sec, row.id, 'down');
                              }}
                            >
                              ↓
                            </Button>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="btn-action mr-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddItem(row.id);
                              }}
                            >
                              + Add Item
                            </Button>
                            <Button
                              variant={row.status === 1 ? 'outline-danger' : 'outline-success'}
                              size="sm"
                              className="btn-action"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStatusToggle('row', row.id, row.status);
                              }}
                            >
                              {row.status === 1 ? 'Deactivate' : 'Activate'}
                            </Button>
                          </div>
                        </div>
                      </Accordion.Header>

                      <Accordion.Body>
                        {[1, 3, 4].includes(sec.secType) ? (
                          row.homedata && row.homedata.length > 0 ? (
                            <Row>
                              {row.homedata.map((item, i) => (
                                <Col md={4} sm={4} xs={6} key={i} className="mb-2 mt-2 text-center">
                                  <Col className="position-relative">
                                    <a
                                      href={`${process.env.REACT_APP_IMAGE_URL}${item.valueData}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <img
                                        src={`${process.env.REACT_APP_IMAGE_URL}${item.valueData}`}
                                        alt={`Banner ${i + 1}`}
                                        className="img-fluid rounded mb-2"
                                        style={{ maxHeight: '150px', objectFit: 'cover' }}
                                      />
                                    </a>

                                    <div
                                      className={`position-absolute top-0 start-0 ${item.status === 1 ? 'bg-success' : 'bg-danger'} text-white px-2 py-1 small rounded-end`}
                                    >
                                      {item.status === 1 ? 'Active' : 'Inactive'}
                                    </div>

                                    <span
                                      className="position-absolute top-0 end-0 px-2 py-1 badge bg-info text-white"
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => handleItemEdit(item)}
                                    >
                                      ✏️ Edit
                                    </span>

                                    <div className="position-absolute bottom-0 start-0 d-flex align-items-center gap-1 p-1">
                                      <span className="badge bg-secondary">#{getNum(item.orderList, i + 1)}</span>
                                      <Button
                                        size="sm"
                                        variant="light"
                                        className="py-0 px-1"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          moveItem(row, item.id, 'up');
                                        }}
                                      >
                                        ↑
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="light"
                                        className="py-0 px-1"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          moveItem(row, item.id, 'down');
                                        }}
                                      >
                                        ↓
                                      </Button>
                                    </div>

                                    <div className="text-start small">
                                      {item.title && (
                                        <div>
                                          <strong>Title:</strong> {item.title}
                                        </div>
                                      )}
                                      {item.slug && (
                                        <div>
                                          <strong>Slug:</strong> {item.slug}
                                        </div>
                                      )}
                                      {item.url && (
                                        <div>
                                          <strong>URL:</strong>{' '}
                                          <a
                                            href={`${process.env.REACT_APP_WEBSITE_URL}${item.url}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            {item.url}
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                </Col>
                              ))}
                            </Row>
                          ) : (
                            <div className="text-center text-muted py-3">🚫 No records found for this row.</div>
                          )
                        ) : row.homedata && row.homedata.length > 0 ? (
                          <Row>
                            {sortByOrder(row.homedata || [], 'orderList').map((product, pi) => (
                              <Col key={product.id} md={4} sm={4} xs={6} className="mb-4">
                                <Card className="h-100 shadow-sm border position-relative">
                                  <div
                                    className={`position-absolute top-0 start-0 ${product.status === 1 ? 'bg-success' : 'bg-danger'} text-white px-2 py-1 small rounded-end`}
                                  >
                                    {product.status === 1 ? 'Active' : 'Inactive'}
                                  </div>

                                  <span
                                    className="position-absolute top-0 end-0 px-2 py-1 badge bg-info text-white"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleItemEdit(product)}
                                  >
                                    ✏️ Edit
                                  </span>

                                  <div className="position-absolute bottom-0 start-0 d-flex align-items-center gap-1 p-1">
                                    <span className="badge bg-secondary">#{getNum(product.orderList, pi + 1)}</span>
                                    <Button
                                      size="sm"
                                      variant="light"
                                      className="py-0 px-1"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        moveItem(row, product.id, 'up');
                                      }}
                                    >
                                      ↑
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="light"
                                      className="py-0 px-1"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        moveItem(row, product.id, 'down');
                                      }}
                                    >
                                      ↓
                                    </Button>
                                  </div>

                                  <div className="position-absolute bottom-0 end-0">
                                    <Button
                                      className={`prodeactBtn rounded-start ${product.status === 1 ? 'btn-danger' : 'btn-success'}`}
                                      size="sm"
                                      onClick={() => handleStatusToggle('homedata', product.id, product.status)}
                                    >
                                      {product.status === 1 ? 'Deactivate' : 'Activate'}
                                    </Button>
                                  </div>

                                  <Card.Body className="d-flex flex-column align-items-center text-center">
                                    {getProductDetails(product)}
                                  </Card.Body>
                                </Card>
                              </Col>
                            ))}
                          </Row>
                        ) : (
                          <div className="text-center text-muted py-3">🚫 No records found for this row.</div>
                        )}
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </Card.Body>
            </div>
          </Collapse>
        </Card>
      ))}

      {showForm && (
        <SectionForm
          section={editingSection}
          themes={activeThemes}
          defaultThemeId={themeId}
          defaultSectionFor={sectionFor}
          onClose={handleFormClose}
        />
      )}

      {showEditModal && (
        <EditItemModal
          show={showEditModal}
          onClose={handleModalClose}
          item={selectedItem}
          onSave={handleItemSave}
          saving={saving}
        />
      )}

      {showBannerEditModal && (
        <BannerEditModal
          show={showBannerEditModal}
          onClose={() => setShowBannerEditModal(false)}
          section={editingSection}
          onSave={async (formData) => {
            setSaving(true);
            try {
              const response = await sectionApi.updateSectionData(formData);
              if (response?.success) {
                setShowBannerEditModal(false);
                fetchSections();
              }
            } catch (err) {
              console.error('Error updating banner:', err);
            } finally {
              setSaving(false);
            }
          }}
          saving={saving}
        />
      )}
    </Container>
  );
}

export default SectionList;

